// liveUpdates — the shell's ONE subscription to the WebSocket the server has
// been broadcasting on all along.
//
// docs/PARITY_2026-09-08.md §4: seven server sites broadcast
// `new_notification` and nothing listened. Both ends were already complete —
// `server/utils/websocket.js` is constructed at server.js:140 and delivers
// per-user (websocket.js:122 only sends to `message.userId` when the message
// names one), `client/src/App.js:86` calls `wsService.connect(token)`, and
// `services/api.js` ships `on`/`off`/`notifyListeners`. The only missing piece
// was a subscriber, so every frame was JSON.parsed and thrown away.
//
// What that cost a person: a crew member's SAFETY report waited up to a minute
// for badges.js's poll to change a number, and office/insight/Notifications.js
// has no interval at all, so the screen itself never refreshed while somebody
// watched it.
//
// ── Why this is an event and must never become a poll ───────────────────────
//
// server/server.js:222 rate-limits /api/ to 1000 requests per 15 minutes PER
// IP, and a crew shares one — an office router, or a carrier NAT in the field.
// badges.js carries the arithmetic: its bell at 60s spends 15 of that budget
// per tab per 15 minutes, and an earlier 15s interval spent 60 — a ui-check
// run hit the limiter and every scenario after it failed with 429.
//
// This hook adds NO timer. It costs one invalidation per message actually
// received, and the messages are human-generated (a report filed, a sheet
// submitted, a job claimed) rather than clocked, so a quiet hour costs
// nothing. react-query also dedupes concurrent refetches of the same key, so
// two notifications landing together are one request, not two.
// `liveUpdates.test.js` pins the no-timer property rather than trusting this
// paragraph.
//
// ── Why two keys ────────────────────────────────────────────────────────────
//
// The nav bell and the Notifications screen sit in DISJOINT key trees:
// badges.js:145 keys its unread query ['badges','unread-notifications'], while
// office/insight/Notifications.js:81-83 keys the list and its own count under
// ['notifications']. Neither is a prefix of the other, so invalidating one
// leaves the other showing a stale number. Both are named here, once, instead
// of each screen learning about the socket separately.

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { wsService } from '../../services/api';

/**
 * The query keys a new notification makes stale, as react-query PREFIXES.
 *
 * Exported so a test — or a later screen — reads the contract instead of
 * restating the strings. `['notifications']` covers the list under every
 * filter and the screen's own unread count; the second is the shell bell.
 */
export const LIVE_NOTIFICATION_KEYS = [
  ['notifications'],
  ['badges', 'unread-notifications'],
];

/**
 * The event this hook wires, and only this one.
 *
 * `broadcast` also carries `job_updated`, `time_entry_started` and
 * `time_entry_stopped` (field.js:586, :653, :697). Those are deliberately NOT
 * subscribed here: they are broadcast to every authenticated client rather
 * than to one person, so wiring them means every phone refetching whenever
 * anybody anywhere clocks in — the rate-limit arithmetic above is exactly what
 * makes that a bad trade, and the screens that would benefit (Dispatch Now)
 * already refetch on a 30s interval of their own. Wire them the day somebody
 * has measured the cost, not before.
 */
const LIVE_EVENT = 'new_notification';

/**
 * Subscribe the shell to per-user notification pushes.
 *
 * Call once, from the shell frame — not from a screen. A second caller would
 * double every invalidation for no benefit, and `wsService` is a module
 * singleton (services/api.js:219) whose `on` APPENDS, so listeners accumulate
 * across mounts unless each one is removed again. The cleanup below is what
 * keeps one message costing one refetch; without it a remounting shell turns
 * an event-driven refresh into the very request storm it was chosen to avoid.
 */
export function useLiveUpdates() {
  const qc = useQueryClient();

  useEffect(() => {
    const refresh = () => {
      for (const queryKey of LIVE_NOTIFICATION_KEYS) qc.invalidateQueries({ queryKey });
    };
    wsService.on(LIVE_EVENT, refresh);
    return () => wsService.off(LIVE_EVENT, refresh);
  }, [qc]);
}

export default useLiveUpdates;
